import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type SVGProps } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  HomeIcon,
  QueueListIcon,
  Squares2X2Icon,
  BriefcaseIcon,
  GlobeAltIcon,
  BanknotesIcon,
  ChartBarIcon,
  InboxStackIcon,
  Cog6ToothIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import type { Screen } from '@screens';
import { useShellSidebar } from '../../canvas/dc/boardChrome';
import { useAuth } from '../../hooks/useAuth';
import { config } from '../../config';
import { useAuthStore } from '../../stores/authStore';
import {
  canvasNavGroups,
  navigationGroups,
  screenHref,
  locateScreen as locate,
  type LocatedScreen as Located,
  type CanvasNavGroup,
  type CanvasNavPage,
} from '../../config/navigation';
import '../../canvas/tokens.css';
import '../../canvas/chrome.css';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

const GROUP_ICONS: Record<string, Icon> = {
  operate: BuildingStorefrontIcon,
  cruise: GlobeAltIcon,
  commercial: BriefcaseIcon,
  money: BanknotesIcon,
  insight: ChartBarIcon,
  workspace: InboxStackIcon,
  admin: Cog6ToothIcon,
};

/** Permission required by an implemented route, from the legacy navigation config. */
const permissionByHref = new Map(
  navigationGroups.flatMap((g) => g.items.filter((i) => i.permission).map((i) => [i.href, i.permission as string])),
);

const DESKTOP_QUERY = '(min-width: 1024px)';

export default function Sidebar() {
  const { pathname } = useLocation();
  const { isOpen, isCollapsed, toggleCollapse, setOpen } = useShellSidebar();
  const { hasPermission } = useAuth();
  const located = useMemo(() => locate(pathname), [pathname]);

  const canSee = useCallback(
    (s: Screen) => {
      const perm = permissionByHref.get(screenHref(s));
      return !perm || hasPermission(perm);
    },
    [hasPermission],
  );

  // Start with the drawer closed on small screens, and close it after navigating.
  useLayoutEffect(() => {
    if (typeof window !== 'undefined' && !window.matchMedia(DESKTOP_QUERY).matches) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerOpen = isOpen;
  useEffect(() => {
    if (!drawerOpen) return;
    if (window.matchMedia(DESKTOP_QUERY).matches) return;
    const opener = document.activeElement as HTMLElement | null;
    drawerRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
      // Keep Tab inside the open drawer.
      if (e.key === 'Tab' && drawerRef.current) {
        const items = drawerRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [drawerOpen, setOpen]);

  return (
    <>
      {/* Drawer, below 1024px */}
      {drawerOpen && (
        <div className="cv lg:hidden">
          <button type="button" className="ch-scrim" onClick={() => setOpen(false)} aria-label="Close navigation" tabIndex={-1} />
          <div
            ref={drawerRef}
            id="primary-navigation-drawer"
            className="fixed inset-y-0 left-0 z-40 w-[min(320px,88vw)]"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <SidebarContent
              located={located}
              pathname={pathname}
              canSee={canSee}
              rail={false}
              onClose={() => setOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Fixed sidebar, 1024px and up */}
      <aside
        className={`cv hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:block ${isCollapsed ? 'lg:w-16' : 'lg:w-72'}`}
        aria-label="Primary navigation"
      >
        <SidebarContent
          located={located}
          pathname={pathname}
          canSee={canSee}
          rail={isCollapsed}
          onToggleCollapse={toggleCollapse}
        />
      </aside>
    </>
  );
}

interface SidebarContentProps {
  located: Located | null;
  pathname: string;
  canSee: (s: Screen) => boolean;
  rail: boolean;
  onClose?: () => void;
  onToggleCollapse?: () => void;
}

function SidebarContent({ located, pathname, canSee, rail, onClose, onToggleCollapse }: SidebarContentProps) {
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(located ? [located.groupId] : []));
  const [openPages, setOpenPages] = useState<Set<string>>(() => new Set(located ? [located.pageId] : []));

  // Reveal the current screen when the route changes (keeps what the user opened).
  useEffect(() => {
    if (!located) return;
    setOpenGroups((prev) => (prev.has(located.groupId) ? prev : new Set(prev).add(located.groupId)));
    setOpenPages((prev) => (prev.has(located.pageId) ? prev : new Set(prev).add(located.pageId)));
  }, [located]);

  const toggleGroup = useCallback((id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const togglePage = useCallback((id: string) => {
    setOpenPages((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Read the user from the store: useAuth() would re-run the session check on every render path.
  const role = useAuthStore((state) => state.user?.role);
  const quick: { label: string; to: string; icon: Icon }[] = [
    { label: 'Home', to: '/home', icon: HomeIcon },
    { label: 'Operations Center', to: '/work', icon: QueueListIcon },
    { label: 'All screens', to: '/platform/screens', icon: Squares2X2Icon },
    // slice: prospecting — shown to managers and admins (UX only; the API and database decide access)
    ...(role === 'manager' || role === 'admin'
      ? [{ label: 'Prospecting', to: '/customers/prospecting', icon: MapPinIcon }]
      : []),
  ];

  return (
    <div className={`ch-side ${rail ? 'ch-rail' : ''}`}>
      <div className="ch-side-h">
        <Link to="/home" className="ch-brand" aria-label="WareHouse home" onClick={onClose}>
          <span className="ch-brand-mk">
            <img
              src={config.app.logo.mark}
              srcSet={`${config.app.logo.mark} 2x, ${config.app.logo.markLarge} 4x`}
              alt=""
              width={32}
              height={32}
            />
          </span>
          {!rail && (
            <span>
              <span className="ch-brand-n" style={{ display: 'block' }}>
                WareHouse
              </span>
              <span className="ch-brand-s" style={{ display: 'block' }}>
                GHRS Operations OS
              </span>
            </span>
          )}
        </Link>
        {onClose && (
          <button type="button" className="ch-ib" onClick={onClose} aria-label="Close navigation" data-autofocus>
            <XMarkIcon aria-hidden="true" />
          </button>
        )}
      </div>

      <nav className="ch-nav" aria-label="Warehouse modules">
        <ul>
          {quick.map((q) => {
            const active = pathname === q.to || (q.to === '/customers/prospecting' && pathname.startsWith(q.to + '/'));
            return (
              <li key={q.to}>
                <Link
                  to={q.to}
                  className="ch-link"
                  aria-current={active ? 'page' : undefined}
                  aria-label={rail ? q.label : undefined}
                  title={rail ? q.label : undefined}
                  onClick={onClose}
                  style={rail ? { justifyContent: 'center' } : undefined}
                >
                  <q.icon aria-hidden="true" />
                  {!rail && <span className="ch-link-t">{q.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>

        {!rail && <div className="ch-sec-l">Areas</div>}
        <ul>
          {canvasNavGroups.map((g) => (
            <NavGroupItem
              key={g.id}
              group={g}
              open={!rail && openGroups.has(g.id)}
              rail={rail}
              isCurrent={located?.groupId === g.id}
              onToggle={() => {
                if (rail) {
                  onToggleCollapse?.();
                  setOpenGroups((prev) => new Set(prev).add(g.id));
                } else toggleGroup(g.id);
              }}
              openPages={openPages}
              onTogglePage={togglePage}
              located={located}
              canSee={canSee}
              onNavigate={onClose}
            />
          ))}
        </ul>
      </nav>

      {onToggleCollapse && (
        <div className="ch-side-f">
          <button
            type="button"
            className="ch-collapse"
            onClick={onToggleCollapse}
            aria-label={rail ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={!rail}
          >
            {rail ? (
              <ChevronRightIcon aria-hidden="true" />
            ) : (
              <>
                <ChevronLeftIcon aria-hidden="true" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

interface NavGroupItemProps {
  group: CanvasNavGroup;
  open: boolean;
  rail: boolean;
  isCurrent: boolean;
  onToggle: () => void;
  openPages: Set<string>;
  onTogglePage: (id: string) => void;
  located: Located | null;
  canSee: (s: Screen) => boolean;
  onNavigate?: () => void;
}

function NavGroupItem({
  group,
  open,
  rail,
  isCurrent,
  onToggle,
  openPages,
  onTogglePage,
  located,
  canSee,
  onNavigate,
}: NavGroupItemProps) {
  const GroupIcon = GROUP_ICONS[group.id] ?? Squares2X2Icon;
  const bodyId = `nav-grp-${group.id}`;
  return (
    <li>
      <button
        type="button"
        className="ch-grp-btn"
        aria-expanded={rail ? undefined : open}
        aria-controls={rail || !open ? undefined : bodyId}
        aria-label={rail ? `${group.label}, expand navigation` : undefined}
        title={rail ? group.label : undefined}
        onClick={onToggle}
        style={isCurrent && rail ? { background: 'var(--cv-hover)' } : undefined}
      >
        <GroupIcon aria-hidden="true" />
        {!rail && (
          <>
            <span>{group.label}</span>
            <span className="ch-grp-n" aria-label={`${group.screenCount} screens`}>
              {group.screenCount}
            </span>
            <ChevronRightIcon className="ch-chev" aria-hidden="true" />
          </>
        )}
      </button>
      {open && (
        <ul id={bodyId} className="ch-grp-body">
          {group.pages.map((p) => (
            <NavPageItem
              key={p.id}
              page={p}
              open={openPages.has(p.id) || group.pages.length === 1}
              single={group.pages.length === 1}
              onToggle={() => onTogglePage(p.id)}
              currentBoard={located?.pageId === p.id ? located.board : null}
              canSee={canSee}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

interface NavPageItemProps {
  page: CanvasNavPage;
  open: boolean;
  single: boolean;
  onToggle: () => void;
  currentBoard: string | null;
  canSee: (s: Screen) => boolean;
  onNavigate?: () => void;
}

const NavPageItem = memo(function NavPageItem({ page, open, single, onToggle, currentBoard, canSee, onNavigate }: NavPageItemProps) {
  const bodyId = `nav-page-${page.id}`;
  const visible = open ? page.screens.filter(canSee) : [];
  // A page with exactly one screen links straight to it.
  if (page.screens.length === 1) {
    const s = page.screens[0];
    if (!canSee(s)) return null;
    return (
      <li>
        <ScreenLink screen={s} current={currentBoard === s.board} label={page.label} onNavigate={onNavigate} />
      </li>
    );
  }
  return (
    <li>
      {!single && (
        <button
          type="button"
          className="ch-page-btn"
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
          onClick={onToggle}
          data-current={currentBoard ? 'true' : undefined}
          title={page.fullName}
        >
          <span className="ch-link-t">{page.label}</span>
          <ChevronRightIcon className="ch-chev" aria-hidden="true" />
        </button>
      )}
      {open && (
        <ul id={bodyId} className="ch-page-body">
          {visible.map((s) => (
            <li key={s.board}>
              <ScreenLink screen={s} current={currentBoard === s.board} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
});

function ScreenLink({
  screen,
  current,
  label,
  onNavigate,
}: {
  screen: Screen;
  current: boolean;
  label?: string;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={screenHref(screen)}
      className="ch-link"
      aria-current={current ? 'page' : undefined}
      onClick={onNavigate}
      title={[screen.id, screen.name].filter(Boolean).join(' · ')}
    >
      <span className="ch-link-t">{label ?? screen.name}</span>
      {screen.wiredWeb && (
        <span className="ch-live" title="An implemented page backs this screen">
          Live
        </span>
      )}
    </Link>
  );
}
